import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ConsumptionAnalyticsActions } from './consumption-analytics.actions';
import { CommodityType } from '@ui/aos-lib';
import { createDynamicMinAndMaxValueAxisLabelsConfig } from 'src/app/shared/utils/chart.utils';
import {
  mapConsumptionDataToSelectedUnit,
  mapTableDataToSelectedUnit,
  mapWidgetConfigToSelectedUnit,
  unitOfMeasureConverter,
  UnitsOfMeasure
} from '../../customers/components/customer-details-consumption/unit-of-measure/units-of-measure.utils';
import { ConsumptionAnalyticsState, initialState } from './consumption-analytics.state';
import { GroupStatus } from 'src/app/pages/administration/report-groups/report-groups.types';
import {
  AsyncRequestStatus,
  requestStatusFailure,
  requestStatusInProgress,
  requestStatusSuccess
} from '@portal/shared/store/request-status';
import { Commodity } from '@portal/shared/types/commodity.types';
import { flowDirectionMultiselectItems } from '@portal/shared/constants/flow-direction.constants';
import { FlowDirection } from '@portal/shared/types/filter.types';
import { ConsumptionReads } from '../../shared/consumption.types';
import { TableData } from '@portal/shared/types/api-response.types';

const consumptionAnalyticsReducer = createReducer(
  initialState,
  on(ConsumptionAnalyticsActions.getReportGroupAndInitFiltersState, (state): ConsumptionAnalyticsState => {
    return {
      ...state,
      filtersLoaded: false
    };
  }),
  on(
    ConsumptionAnalyticsActions.initFilters,
    (state, { reportGroups: reportGroupsResponse, filtersState, dateRange, restoredTake }) => {
      const reportGroups = reportGroupsResponse.map(({ groupName, groupUID, groupStatus }) => ({
        text: groupName,
        value: groupUID,
        isDisabled: groupStatus === GroupStatus.Calculating
      }));
      const selectedReportGroupItem = reportGroups.find(group => group.value === filtersState?.reportGroupUID);
      const selectedCommodity = reportGroupsResponse.find(
        group => group.groupUID === filtersState?.reportGroupUID
      )?.commodity;

      return {
        ...state,
        filtersLoaded: true,
        reportGroupFilter: {
          ...state.reportGroupFilter,
          items: reportGroups,
          selected: selectedReportGroupItem ? [selectedReportGroupItem] : [reportGroups[0]]
        },
        reportGroups: reportGroupsResponse,
        commodity: selectedCommodity ?? reportGroupsResponse?.[0].commodity,
        previousPeriod: filtersState?.previousPeriod ?? state.previousPeriod,
        dateRange,
        ...(restoredTake !== undefined && {
          tableState: { ...state.tableState, take: restoredTake }
        }),
        flowDirectionFilter: {
          ...state.flowDirectionFilter,
          items: flowDirectionMultiselectItems,
          selected: filtersState?.flowDirection?.length
            ? flowDirectionMultiselectItems.filter(item =>
                filtersState.flowDirection!.includes(item.value as FlowDirection)
              )
            : [flowDirectionMultiselectItems[0]]
        }
      };
    }
  ),
  on(ConsumptionAnalyticsActions.getWidgetConsumptions, state => ({
    ...state,
    widgetRequest: requestStatusInProgress()
  })),
  on(ConsumptionAnalyticsActions.getWidgetConsumptionsSuccess, (state, { widget }) => ({
    ...state,
    widgetRequest: requestStatusSuccess(),
    widget
  })),
  on(ConsumptionAnalyticsActions.getWidgetConsumptionsFailure, (state, { error }) => ({
    ...state,
    widget: initialState.widget,
    widgetRequest: requestStatusFailure(error)
  })),
  on(ConsumptionAnalyticsActions.setFilters, (state, { reportGroupUID, dateRange, previousPeriod, flowDirection }) => {
    const selectedCommodity = reportGroupUID
      ? (state.reportGroups.find(({ groupUID }) => groupUID === reportGroupUID)?.commodity ?? '')
      : state.commodity;

    return {
      ...state,
      ...(dateRange && { dateRange }),
      ...(reportGroupUID && {
        consumptions: initialState.consumptions,
        consumptionsWithUOM: initialState.consumptionsWithUOM,
        consumptionReads: initialState.consumptionReads,
        consumptionReadsWithUOM: initialState.consumptionReadsWithUOM,
        chartSettings: initialState.chartSettings
      }),
      previousPeriod:
        selectedCommodity === Commodity.Electricity
          ? null
          : previousPeriod
            ? (previousPeriod.value as string)
            : state.previousPeriod,
      ...(reportGroupUID && {
        reportGroupFilter: {
          ...state.reportGroupFilter,
          selected: [state.reportGroupFilter.items.find(item => item.value === reportGroupUID)!]
        },
        commodity: selectedCommodity
      }),
      tableState: {
        ...state.tableState,
        skip: 0
      },
      ...(flowDirection && {
        flowDirectionFilter: {
          ...state.flowDirectionFilter,
          selected: state.flowDirectionFilter.items.filter(item => flowDirection.includes(item.value as FlowDirection))!
        }
      })
    };
  }),
  on(ConsumptionAnalyticsActions.getConsumptions, state => ({
    ...state,
    consumptionsRequest: requestStatusInProgress()
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsSuccess, (state, { consumptions }) => ({
    ...state,
    consumptionsRequest: requestStatusSuccess(),
    consumptions: [...consumptions]
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsFailure, (state, { error }) => ({
    ...state,
    consumptions: initialState.consumptions,
    consumptionsRequest: requestStatusFailure(error)
  })),
  on(ConsumptionAnalyticsActions.getConsumptionReads, state => ({
    ...state,
    consumptionReadsRequest: requestStatusInProgress()
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsReadsSuccess, (state, { consumptionReads }) => ({
    ...state,
    consumptionReadsRequest: requestStatusSuccess(),
    consumptionReads: { ...consumptionReads }
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsReadsFailure, (state, { error }) => ({
    ...state,
    consumptionReads: initialState.consumptionReads,
    consumptionReadsRequest: requestStatusFailure(error)
  })),
  on(ConsumptionAnalyticsActions.setTableState, (state, { tableState }) => ({
    ...state,
    tableState
  })),
  on(ConsumptionAnalyticsActions.setUnitOfMeasureModel, (state, { model, reportGroupUID }) => {
    const { model: prevUnitOfMeasureModel, reportGroupUID: prevReportGroupUID } = state.unitOfMeasure;
    const shouldPersistUnitOfMeasureModelSelectedItem =
      !(model.selectedItem.value === 'Select' || prevUnitOfMeasureModel.selectedItem.value === 'Select') &&
      reportGroupUID === prevReportGroupUID;
    const selectedItem = shouldPersistUnitOfMeasureModelSelectedItem
      ? prevUnitOfMeasureModel.selectedItem
      : model.selectedItem;

    return {
      ...state,
      unitOfMeasure: {
        ...state.unitOfMeasure,
        model: {
          ...state.unitOfMeasure.model,
          ...model,
          selectedItem,
          isDisabled: (shouldPersistUnitOfMeasureModelSelectedItem ? prevUnitOfMeasureModel : model).isDisabled
        },
        multiplier: unitOfMeasureConverter(
          state.commodity as CommodityType,
          state.widget.unitOfMeasure as UnitsOfMeasure,
          selectedItem.value as UnitsOfMeasure
        ),
        ...(reportGroupUID && { reportGroupUID })
      }
    };
  }),
  on(ConsumptionAnalyticsActions.buildWidgetWithUom, state => {
    const consumptionOverviewWithUOM = state.widget
      ? mapWidgetConfigToSelectedUnit(
          state.widget,
          state.unitOfMeasure.multiplier,
          state.unitOfMeasure.model.selectedItem.text
        )
      : initialState.widgetWithUOM;

    return {
      ...state,
      widgetWithUOM: { ...consumptionOverviewWithUOM }
    };
  }),
  on(ConsumptionAnalyticsActions.buildChartWithUom, state => {
    const consumptionsWithUOM = state.consumptions
      ? mapConsumptionDataToSelectedUnit(
          state.consumptions.map(deviceHealth => ({
            ...deviceHealth,
            health: {
              ...deviceHealth.health,
              unitOfMeasure: state.unitOfMeasure.model.selectedItem.text
            }
          })),
          state.unitOfMeasure.multiplier
        )
      : initialState.consumptions;

    return {
      ...state,
      consumptionsWithUOM,
      chartSettings: {
        ...state.chartSettings,
        valueAxisLabelsConfig: createDynamicMinAndMaxValueAxisLabelsConfig(consumptionsWithUOM)
      }
    };
  }),
  on(ConsumptionAnalyticsActions.buildTableWithUom, state => {
    const reads = state.consumptionReads as TableData<ConsumptionReads>;
    const consumptionReadsWithUOM = reads
      ? {
          ...reads,
          data: [...mapTableDataToSelectedUnit(reads.data, state.unitOfMeasure.multiplier)]
        }
      : (initialState.consumptionReads as TableData<ConsumptionReads>);
    return {
      ...state,
      consumptionReadsWithUOM
    };
  }),
  on(ConsumptionAnalyticsActions.setChartNumbersShown, (state, { isShown }) => ({
    ...state,
    isChartNumbersShown: isShown
  })),
  on(ConsumptionAnalyticsActions.clearConsumption, state => ({
    ...state,
    consumptionsWithUOM: initialState.consumptionsWithUOM,
    consumptions: initialState.consumptions,
    consumptionReadsWithUOM: initialState.consumptionReadsWithUOM,
    consumptionReads: initialState.consumptionReads,
    widgetWithUOM: initialState.widgetWithUOM,
    widget: initialState.widget
  })),
  on(ConsumptionAnalyticsActions.getConsumptionReadsElectric, state => ({
    ...state,
    consumptionReadsRequest: requestStatusInProgress()
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsReadsElectricSuccess, (state, { consumptionReads }) => ({
    ...state,
    consumptionReadsRequest: requestStatusSuccess(),
    consumptionReads: { ...consumptionReads },
    consumptionReadsWithUOM: { ...consumptionReads }
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsReadsElectricFailure, (state, { error }) => ({
    ...state,
    consumptionReads: initialState.consumptionReads,
    consumptionReadsRequest: requestStatusFailure(error)
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsElectric, state => ({
    ...state,
    consumptionsRequest: requestStatusInProgress()
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsElectricSuccess, (state, { consumptions }) => ({
    ...state,
    consumptionsRequest: requestStatusSuccess(),
    consumptions: [...consumptions]
  })),
  on(ConsumptionAnalyticsActions.getConsumptionsElectricFailure, (state, { error }) => ({
    ...state,
    consumptions: initialState.consumptions,
    consumptionsRequest: requestStatusFailure(error)
  }))
);

export const consumptionAnalyticsFeature = createFeature({
  name: 'consumptionAnalytics',
  reducer: consumptionAnalyticsReducer,
  extraSelectors: ({
    selectWidgetRequest,
    selectConsumptionsRequest,
    selectConsumptionReadsRequest,
    selectReportGroupFilter,
    selectDateRange,
    selectPreviousPeriod,
    selectInterval,
    selectFlowDirectionFilter,
    selectCommodity
  }) => ({
    selectIsWidgetLoading: createSelector(
      selectWidgetRequest,
      request => request.status === AsyncRequestStatus.InProgress
    ),
    selectWidgetError: createSelector(selectWidgetRequest, request => request.error),
    selectIsChartLoading: createSelector(
      selectConsumptionsRequest,
      request => request.status === AsyncRequestStatus.InProgress
    ),
    selectChartError: createSelector(selectConsumptionsRequest, request => request.error),
    selectIsTableLoading: createSelector(
      selectConsumptionReadsRequest,
      request => request.status === AsyncRequestStatus.InProgress
    ),
    selectConsumptionReadsError: createSelector(selectConsumptionReadsRequest, request => request.error),
    selectWidgetConsumptionsFilters: createSelector(
      selectReportGroupFilter,
      selectDateRange,
      selectPreviousPeriod,
      selectInterval,
      selectFlowDirectionFilter,
      selectCommodity,
      (reportGroupFilter, dateRange, previousPeriod, interval, flowDirection, commodity) =>
        dateRange
          ? {
              reportGroupUIDs: [reportGroupFilter.selected[0]?.value as string],
              startDateUtc: dateRange.start,
              endDateUtc: dateRange.end,
              comparisonPeriod: previousPeriod,
              interval,
              ...(commodity === Commodity.Electricity && {
                flowDirection: flowDirection.selected.map(item => item.value as FlowDirection)
              })
            }
          : null
    )
  })
});

export const {
  selectReportGroupFilter,
  selectDateRange,
  selectPreviousPeriod,
  selectIsChartLoading,
  selectIsTableLoading,
  selectInterval,
  selectTableState,
  selectChartSettings,
  selectWidgetWithUOM,
  selectConsumptionReads,
  selectConsumptionReadsWithUOM,
  selectConsumptionsWithUOM,
  selectUnitOfMeasure,
  selectIsChartNumbersShown,
  selectFiltersLoaded,
  selectCommodity,
  selectIsWidgetLoading,
  selectWidgetError,
  selectChartError,
  selectConsumptionReadsError,
  selectWidgetConsumptionsFilters,
  selectFlowDirectionFilter
} = consumptionAnalyticsFeature;
